import BookIndex from "components/shared/book/list";
import InlineBtn from "components/shared/inlineBtn";
import Loader from "components/shared/loader";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import Table from "components/shared/table";
import ru from "date-fns/locale/ru";
import { parseDateString } from "helpers/date";
import { workloadStatusesEnum } from "helpers/enums";
import { DebouncedDefaultColumnFilter, DebouncedRangeColumnFilter, SelectColumnFilter } from "helpers/reactTable";
import { alphabeticalSort } from "helpers/system";
import debounce from "lodash/debounce";
import memoize from "lodash/memoize";
import upperFirst from "lodash/upperFirst";
import { matchSorter } from "match-sorter";
import moment from "moment";
import React from "react";
import DatePicker from "react-datepicker";
import InputMask from "react-input-mask";
import { alert } from "services/alert";
import SignalRConnection from "services/signalRConnection";
import { getCarsInfo } from "sources/books/trucks";
import connect from "./listConnect";

const tableDefaultProps = {
    minRows: 10,
    noDataText: "Нет данных для отображения",
    loadingComponent: <Loader />,
    pageSizeOptions: [30, 50, 100],
};

class ClientStatisticsList extends BookIndex {
    state = {
        startedAt: moment().subtract(14, "d").startOf("d"),
        endedAt: moment().endOf("d"),
        original: [],
        items: [],
        filtered: {},
        isLoading: false,
    };

    constructor(props) {
        super(props);

        this.tableColumns = memoize(this.tableColumns);
    }

    componentDidMount() {
        this.mounted = true;
        return this.loadItems();
    }

    componentWillUnmount() {
        this.mounted = false;
        if (this.connection && this.isConnected) {
            this.connection.disconnect();
        }
    }

    getKey = () => {
        const { startedAt, endedAt } = this.state;
        return getCarsInfo({ startedAt, endedAt }).then(({ key }) => key);
    };

    loadItems = () => {
        if (this.connection && this.isConnected) {
            this.connection.disconnect();
        }
        this.setState({ isLoading: true, items: [] });
        this.getKey().then((key) => {
            if (!this.mounted) {
                return;
            }
            this.isBlocked = false;
            this.isConnected = true;
            this.connection = new SignalRConnection({
                url: "/signalr/syncWorks",
                connectionParams: key,
                actions: [{ name: "SetStatus", action: (status) => this.applySetState(status, key) }],
                joinActionName: "Subscribe",
                leaveActionName: "Unsubscribe",
            });
        });
    };

    loadItemsDebounce = debounce(this.loadItems, 300);

    loadItemsDebounceWrap = () => {
        this.setState({ isLoading: false }, this.loadItemsDebounce);
    };

    applySetState = (status, key) => {
        const { startedAt, endedAt } = this.state;
        if (this.isBlocked) {
            return;
        }
        this.isBlocked = true;
        this.isConnected = false;
        this.connection.disconnect();
        if (status === workloadStatusesEnum.Completed) {
            getCarsInfo({ key, startedAt, endedAt }).then(({ result }) => {
                const newItems = this.filterItems(result, this.state.filtered);
                this.setState({ items: newItems, original: result, isLoading: false, dataLoadedAt: Date.now() });
            });
        } else if (status === workloadStatusesEnum.Faulted) {
            alert({ message: "Ошибка", type: "error" });
            this.setState({ items: [], original: [], isLoading: false });
        }
    };

    renderCounter = () => null;

    tableColumns = () => {
        return [
            {
                Header: "#",
                id: "row_number",
                width: 50,
                sortable: false,
                notExportable: true,
                disableSortBy: true,
            },
            {
                Header: "Организация",
                accessor: "organizationName",
                sortType: (rowA, rowB, columnId) => alphabeticalSort(rowA.original, rowB.original, columnId),
                filterable: true,
                isMulti: true,
                filterType: "arrayContains",
                Filter: (props) => {
                    return (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                ...defaultMultipleSelectProps,
                                options: this.state.original.map((x) => ({
                                    label: x.organizationName,
                                    value: x.organizationName,
                                })),
                                inputProps: { name: "organizationName" },
                                components: { ValueContainer },
                            }}
                        />
                    );
                },
            },
            {
                Header: "Общее кол-во машин",
                accessor: "carsCount",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "Кол-во машин с активным пробегом",
                accessor: "activeCarsCount",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "Кол-во машин из общего отчёта",
                accessor: "carsInOverallReportCount",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "Кол-во машин участвующих в планировании заправок",
                accessor: "countOfCarsInvolvedInRefuelingPlanning",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: `${upperFirst(this.props.organization?.currencyData?.name)}/час`,
                accessor: "outputPerHour",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "% машин с акт.пробегом от общего числа",
                accessor: "percentOfActiveCars",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "% машин из общего отчёта от общего числа",
                accessor: "percentOfCarsInOverallReport",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "% использования приложения водителями",
                accessor: "driverAppUsageAveragePercent",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: "% своевременного заведения заявок",
                accessor: "bidsCreatedOnTimePercent",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
            {
                Header: `Выработка, ${this.props.organization?.currencyData?.name}/км`,
                accessor: "output",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
            },
        ];
    };

    setDateValue = (name, value) => {
        if (!value) {
            value = undefined;
        }
        if (name === "startedAt" && this.state.endedAt < value) {
            this.setState({
                [name]: value,
                endedAt: moment(value).add(1, "day").endOf("day"),
            });
        } else {
            this.setState({ [name]: moment(value) });
        }
    };

    onChangeRaw = (name, e) => this.setDateValue(name, parseDateString(e.target.value));

    renderActions = () => {
        const defaultProps = {
            className: "field-wrap__input field-wrap__input--small",
            dateFormat: "dd.MM.yyyy HH:mm",
            timeCaption: "Время",
            showTimeSelect: true,
            timeIntervals: 5,
            timeFormat: "HH:mm",
            locale: ru,
            customInput: <InputMask mask="99.99.9999 99:99" />,
        };
        return (
            <div className="d-flex justify-content-between">
                <div className="d-flex">
                    <div className="mr-2">Период</div>
                    <div className="mr-2">
                        <DatePicker
                            onChange={this.setDateValue.bind(this, "startedAt")}
                            name="startedAt"
                            selected={this.state.startedAt?.toDate()}
                            placeholderText="Начало"
                            onChangeRaw={this.onChangeRaw.bind(this, "startedAt")}
                            {...defaultProps}
                        />
                    </div>
                    <div className="mr-2">&mdash;</div>
                    <div className="mr-2">
                        <DatePicker
                            onChange={this.setDateValue.bind(this, "endedAt")}
                            name="endedAt"
                            selected={this.state.endedAt?.toDate()}
                            placeholderText="Конец"
                            onChangeRaw={this.onChangeRaw.bind(this, "endedAt")}
                            minDate={this.state.startedAt?.toDate()}
                            {...defaultProps}
                        />
                    </div>
                    <div className="align-items-baseline">
                        <InlineBtn value="Обновить" cname="refresh" active action={this.loadItemsDebounceWrap} />
                    </div>
                </div>
            </div>
        );
    };

    filterItems = (original, filtered) => {
        return Object.values(filtered).reduce((memo, { id, value, filterType, isMulti }) => {
            if (!value) {
                return memo;
            }
            if (!["numberRange"].includes(filterType) && !isMulti && value) {
                value = value.trim();
            }
            switch (filterType) {
                case "numberRange": {
                    return memo.filter((item) => {
                        let val = true;
                        if (value.start) {
                            val = item[id] >= value.start;
                        }
                        if (value.end) {
                            val = val && item[id] <= value.end;
                        }
                        return val;
                    });
                }
                case "arrayContains": {
                    if (!value.length) {
                        return memo;
                    }
                    return memo.filter((item) => value.includes(item.organizationName));
                }
                default:
                    return memo.filter(
                        (item) =>
                            matchSorter([item[id]], value, { threshold: matchSorter.rankings.CONTAINS }).length !== 0,
                    );
            }
        }, original);
    };

    onColumnFilterChange = () => {
        const newItems = this.filterItems(this.state.original, this.state.filtered);
        this.setState({ items: newItems });
    };

    renderListComponent = () => {
        const reportProps = {
            data: this.state.items,
            columns: this.tableColumns(),
            loading: this.state.isLoading,
            defaultColumn: {
                sortable: true,
                Filter: DebouncedDefaultColumnFilter,
            },
            filtered: this.state.filtered,
            onFilteredChange: ({ id, ...rest }) => {
                const newFilter = this.state.filtered;
                newFilter[id] = { id, ...rest };
                this.setState({ filtered: newFilter });
                this.onColumnFilterChange();
            },
        };
        return (
            <div className="list__body-wrap pb-3 container-fluid">
                <div className="list__body d-flex flex-column">
                    <Table {...tableDefaultProps} {...reportProps} />
                </div>
            </div>
        );
    };
}

export default connect(ClientStatisticsList);
